import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { OAuthService } from 'angular-oauth2-oidc';
import { take } from 'rxjs/operators';

import { BookmarkClickAnalyticsEvent } from '@app/public/public/bookmarks/state/bookmark-click-analytics-event';
import { ClassroomService } from '@app/public/public/classroom/services/classroom.service';
import { environment } from '@environment/environment';

import { UserFeatureEngagement } from './UserFeatureEngagement';
import { AppUnit } from './app-unit';
import { PlatformType } from './platform-type';
import { UserActivity } from './user-activity';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private currentFeature: AppUnit;
    private currentRoute: string;
    private startTime: number;
    private previousFeature: AppUnit;

    private pendingEngagements: UserFeatureEngagement[] = [];
    private engagementFlushTimer: ReturnType<typeof setTimeout>;
    private readonly engagementFlushInterval = 60000; // 1 minute

    private readonly maxBatchSize = 10;
    private isFlushing = false;
    private hiddenStartedAt: number;
    private hiddenTimeoutTimer: ReturnType<typeof setTimeout>;

    private readonly normalFeatureHiddenTimeout = 30 * 60 * 1000; // 30 min

    private currentSessionId: string;
    private zoomMeetingTimer: ReturnType<typeof setTimeout>;

    constructor(
        private httpClient: HttpClient,
        private oAuthService: OAuthService,
        private classroomService: ClassroomService,
    ) {
        this.setupListeners();
    }

    startTracking(feature: AppUnit, route: string): void {
        const isSameTrack =
            this.currentFeature === feature && this.currentRoute === route;

        if (!isSameTrack) {
            this.stopTracking('route');
            if (
                this.isZoomMeetingRoute(route) &&
                this.pendingEngagements.length > 0
            ) {
                this.flushEngagements();
            }

            this.currentFeature = feature;
            this.currentRoute = route;

            this.loadZoomSessionDuration(route);

            this.saveUserActivity();
        }

        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }

    private isZoomMeetingRoute(route?: string): boolean {
        return route?.startsWith('/classroom/');
    }

    private isWherebyMeetingRoute(route?: string): boolean {
        return route?.startsWith('/whereby-classroom/');
    }

    private loadZoomSessionDuration(route: string): void {
        clearTimeout(this.zoomMeetingTimer);

        if (!this.isZoomMeetingRoute(route)) {
            this.currentSessionId = null;
            return;
        }

        const sessionId = route.split('/')[2];

        if (!sessionId || this.currentSessionId === sessionId) {
            return;
        }

        this.currentSessionId = sessionId;

        this.classroomService
            .getById(sessionId)
            .pipe(take(1))
            .subscribe({
                next: (session) => {
                    const sessionEndTime = session.startDateTime
                        .clone()
                        .add(session.duration, 'minutes');

                    const remainingMs = sessionEndTime.valueOf() - Date.now();

                    // Session already ended
                    if (remainingMs <= 0) {
                        return;
                    }

                    this.zoomMeetingTimer = setTimeout(() => {
                        if (
                            this.currentRoute &&
                            this.isZoomMeetingRoute(this.currentRoute)
                        ) {
                            this.stopTracking('route');
                        }
                    }, remainingMs);
                },
                error: (err) => {
                    console.error(
                        '[Analytics] Failed loading Zoom session',
                        err,
                    );
                },
            });
    }

    // STOP TRACKING
    // Only send activity when the user really leaves the feature.
    // reason:
    //  - 'route'   => user navigated to another module (triggered by startTracking)
    //  - 'unload'  => tab/page closing (beforeunload)
    stopTracking(reason: 'route' | 'unload' = 'route'): void {
        if (!this.startTime) {
            return;
        }

        const endTime = Date.now();
        const elapsedSeconds = Math.floor((endTime - this.startTime) / 1000);

        if (elapsedSeconds < 2) {
            clearTimeout(this.zoomMeetingTimer);
            clearTimeout(this.hiddenTimeoutTimer);
            this.currentSessionId = null;
            this.startTime = null;
            return;
        }

        const engagement: UserFeatureEngagement = {
            appUnit: this.currentFeature,
            platformType: PlatformType.Web,
            visitDateTime: new Date(this.startTime),
            leaveDateTime: new Date(endTime),
            durationSeconds: elapsedSeconds,
        };

        if (reason === 'unload') {
            const engagements = [...this.pendingEngagements, engagement];
            this.sendEngagementKeepAlive(engagements);
            this.pendingEngagements = [];
            clearTimeout(this.engagementFlushTimer);
            this.engagementFlushTimer = null;
            this.isFlushing = false;
        } else {
            this.pendingEngagements.push(engagement);
            if (this.pendingEngagements.length >= this.maxBatchSize) {
                clearTimeout(this.engagementFlushTimer);
                this.engagementFlushTimer = null;

                this.flushEngagements();
            } else {
                this.scheduleEngagementFlush();
            }
        }

        clearTimeout(this.zoomMeetingTimer);
        clearTimeout(this.hiddenTimeoutTimer);
        this.currentSessionId = null;
        this.startTime = null;
    }

    // RECORDING WATCH TRACKING
    // Watching a recording of a past class is tracked as its own activity
    // (AppUnit.Recordings). While the recording is playing, the page-level
    // activity (e.g. ClassDetails / ClassMaterials) is paused; it resumes as
    // soon as the recording is paused. The watch time is flushed (posted) when
    // the recording is paused.
    trackRecordingStarted(): void {
        // if we are already tracking a recording, stay as is.
        if (this.currentFeature === AppUnit.Recordings) {
            return;
        }

        // Remember the page we were tracking so we can resume it on pause.
        this.previousFeature = this.currentFeature;

        // Flush the page-level activity and switch to the recording watch.
        this.stopTracking('route');
        this.currentFeature = AppUnit.Recordings;

        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }

    trackRecordingPaused(): void {
        if (this.currentFeature !== AppUnit.Recordings) {
            return;
        }

        this.stopTracking('route');

        // Resume the page-level activity that was paused when the recording
        // started (ClassDetails / ClassMaterials / whatever was active).
        this.currentFeature = this.previousFeature ?? AppUnit.ClassDetails;
        this.previousFeature = null;

        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }

    private saveUserActivity(): void {
        if (!environment.userAnalyticsEnabled) {
            return;
        }

        if (this.currentFeature == null || !this.currentRoute) {
            return;
        }

        const activity: UserActivity = {
            appUnit: this.currentFeature,
            route: this.currentRoute,
            platformType: PlatformType.Web,
        };

        this.httpClient
            .post(environment.apiUrl + '/an/user-activities', activity)
            .pipe(take(1))
            .subscribe({
                error: (err) => {
                    console.error(
                        '[AnalyticsService] user activity API error:',
                        err,
                    );
                },
            });
    }

    private scheduleEngagementFlush(): void {
        if (this.engagementFlushTimer) {
            return;
        }

        this.engagementFlushTimer = setTimeout(() => {
            this.flushEngagements();
        }, this.engagementFlushInterval);
    }

    private flushEngagements(): void {
        if (this.isFlushing) {
            return;
        }

        if (!this.pendingEngagements.length) {
            return;
        }

        this.isFlushing = true;

        const engagements = [...this.pendingEngagements];

        this.pendingEngagements = [];

        this.engagementFlushTimer = null;

        this.httpClient
            .post(
                environment.apiUrl +
                    '/an/user-activities/feature-engagement/batch',
                engagements,
            )
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.engagementFlushTimer = null;
                    this.isFlushing = false;
                },
                error: () => {
                    this.pendingEngagements.unshift(...engagements);
                    this.engagementFlushTimer = null;
                    this.isFlushing = false;
                    this.scheduleEngagementFlush();
                },
            });
    }

    private sendEngagementKeepAlive(
        engagements: UserFeatureEngagement[],
    ): void {
        const url =
            environment.apiUrl + '/an/user-activities/feature-engagement/batch';
        const token = this.oAuthService.getAccessToken();

        fetch(url, {
            method: 'POST',
            keepalive: true,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(engagements),
        });
    }

    private setupListeners(): void {
        document.addEventListener('visibilitychange', () => {
            // Zoom, Whereby and Chat continue tracking.
            if (
                this.isZoomMeetingRoute(this.currentRoute) ||
                this.isWherebyMeetingRoute(this.currentRoute) ||
                this.currentFeature === AppUnit.ChatRoom
            ) {
                return;
            }

            if (document.hidden) {
                this.hiddenStartedAt = Date.now();
                clearTimeout(this.hiddenTimeoutTimer);
                this.hiddenTimeoutTimer = setTimeout(() => {
                    if (document.hidden) {
                        this.stopTracking('route');
                    }
                }, this.normalFeatureHiddenTimeout);
            } else {
                clearTimeout(this.hiddenTimeoutTimer);

                if (this.hiddenStartedAt != null) {
                    if (this.startTime) {
                        const hiddenDuration =
                            Date.now() - this.hiddenStartedAt;

                        this.startTime += hiddenDuration;
                    } else {
                        // Hidden timeout ended previous session.
                        // Start a new one from now.
                        this.startTime = Date.now();
                    }
                }

                this.hiddenStartedAt = null;
            }
        });

        // Zoom launch triggers beforeunload.
        // Ignore unloads while on Zoom routes and
        // rely on scheduled-duration tracking instead.
        window.addEventListener('beforeunload', () => {
            if (this.isZoomMeetingRoute(this.currentRoute)) {
                return;
            }

            this.stopTracking('unload');
        });
    }

    addBookmarkClicks(
        bookmarkClickAnalyticsEvent: BookmarkClickAnalyticsEvent,
    ): void {
        this.httpClient
            .post<BookmarkClickAnalyticsEvent>(
                environment.apiUrl + '/an/bookmarks/events/click',
                bookmarkClickAnalyticsEvent,
            )
            .subscribe();
    }
}
